import { useRef } from 'react'
import clsx from 'clsx'
import { ChevronUp, ChevronDown } from '@/assets/icons'
import { useCollapseAnimation } from './useCollapseAnimation'

export type SideNavGroupProps = {
  /** The label text displayed in the group header */
  label: string
  /** Icon component to display (from @/assets/icons) */
  icon?: React.FC<React.SVGProps<SVGSVGElement>>
  /** Whether to show the beta tag */
  showBetaTag?: boolean
  /** Whether the group is expanded (controlled by parent) */
  isExpanded: boolean
  /** Callback when expand/collapse button is clicked */
  onExpandToggle: () => void
  /** Click handler for the group header (optional, separate from expand toggle) */
  onClick?: () => void
  /** Whether the group is disabled */
  disabled?: boolean
  /** Sub-items to render inside the collapsible section (use SideNavSubItem) */
  children: React.ReactNode
}

/**
 * A collapsible side navigation group that contains sub-items.
 *
 * The group header has an expand/collapse button. When expanded, children
 * are shown with a continuous left border connecting them visually.
 *
 * Use SideNavSubItem components as children.
 */
export function SideNavGroup({ label, icon: Icon, showBetaTag = false, isExpanded, onExpandToggle, onClick, disabled = false, children }: SideNavGroupProps) {
  const contentRef = useRef<HTMLDivElement>(null)
  useCollapseAnimation(contentRef, isExpanded)

  const handleHeaderClick = () => {
    if (disabled) return
    onExpandToggle()
    onClick?.()
  }

  return (
    <div>
      {/* Group header - clicking anywhere toggles expand/collapse */}
      <button
        type="button"
        onClick={handleHeaderClick}
        disabled={disabled}
        aria-expanded={isExpanded}
        className={clsx(
          'group flex items-center p-2 rounded-md w-full gap-2 text-sm font-semibold leading-5 h-10',
          'text-brand-neutral-950 hover:bg-brand-neutral-100',
          'focus:outline-none focus-visible:outline-none focus-visible:shadow-[0px_0px_0px_2px_rgba(42,73,255)_inset]',
          disabled && 'cursor-not-allowed opacity-50'
        )}
      >
        {Icon && <Icon className="h-5 w-5 text-brand-neutral-600 group-hover:text-brand-neutral-700" aria-hidden="true" />}

        <span className="font-rethink-sans">{label}</span>

        {showBetaTag && (
          <span className="rounded bg-brand-neutral-950 px-1 py-px text-[10px] font-medium leading-[11px] text-white [font-variant:small-caps]">beta</span>
        )}

        {/* Chevron indicator (visual only) */}
        <span
          className="ml-auto flex h-5 w-5 items-center justify-center rounded-full border border-brand-neutral-300 bg-brand-neutral-200 group-hover:bg-brand-neutral-300"
          aria-hidden="true"
        >
          {isExpanded ? <ChevronUp className="h-4 w-4 text-brand-neutral-700" /> : <ChevronDown className="h-4 w-4 text-brand-neutral-700" />}
        </span>
      </button>

      {/* Collapsible content with continuous border */}
      <div ref={contentRef} className="mt-1">
        <div className="pl-5">
          <div className="pl-3 border-l-2 border-l-brand-neutral-200">
            <div className="flex flex-col gap-1">{children}</div>
          </div>
        </div>
      </div>
    </div>
  )
}
